import { useAtom } from '@reatom/npm-react'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { premiumConfigAtom } from '@root/entities/premium'
import { BuyPremiumIcon } from '@root/entities/premium/ui'

import type { PremiumMeta } from '@root/entities/premium'

type Props = {
  onSubscriptionInfoClick: () => void
  onBoostersInfoClick: () => void
}

/**
 * @description Hero version of PremiumProducts with custom styling
 */
export const PremiumHeroProducts: React.FC<Props> = ({
  onBoostersInfoClick,
  onSubscriptionInfoClick,
}) => {
  const { t } = useTranslation()
  const [{ premiumBoosters, boosterPriceSol }] = useAtom(premiumConfigAtom)
  const render = (
    product: PremiumMeta.PremiumProduct,
    title: React.ReactElement,
    description: string,
  ) => (
    <>
      <Group>
        <BuyPremiumIcon product={product} size={28} sizeIcon={12} />
        <Text>
          <Title $product={product}>{title}</Title>
        </Text>
      </Group>
      <Description>{description}</Description>
    </>
  )

  return (
    <Root>
      <Product onClick={onSubscriptionInfoClick}>
        <Glow data-product="subscription" />

        {render(
          'subscription',
          <Trans
            i18nKey="premium.product.premium-title"
            components={[<span key={0} />]}
          />,
          t('premium.product.premium-text', {
            boosts: premiumBoosters,
          }),
        )}
      </Product>
      <Product onClick={onBoostersInfoClick}>
        <Glow data-product="boosters" />
        {render(
          'boosters',
          <Trans
            i18nKey="premium.product.booster-title"
            components={[<span key={0} />]}
          />,
          t('premium.product.booster-text', {
            price: boosterPriceSol,
          }),
        )}
      </Product>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr 1fr;
`

const Product = styled.div`
  border: 1px solid rgba(var(--appearance-inv-1-rgb), 0.06);
  background: linear-gradient(
    180deg,
    rgba(var(--appearance-inv-1-rgb), 0.06) 0%,
    rgba(var(--appearance-inv-1-rgb), 0.02) 57.92%
  );

  display: grid;
  overflow: hidden;
  cursor: pointer;

  backdrop-filter: blur(100px);
  border-radius: 12px;
  padding: 14px 18px;
  gap: 4px;
  min-height: 72px;
`

const Group = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: auto 1fr;

  gap: 8px;
`

const Text = styled.span`
  display: grid;
  gap: 3px;
`

const Glow = styled.span`
  &[data-product='subscription'] {
    background-image: radial-gradient(
      70% 100% at 0 0%,
      rgba(var(--subscriptionGlow-9-rgb), 0.2) 10%,
      transparent 100%
    );
  }
  &[data-product='boosters'] {
    background-image: radial-gradient(
      70% 100% at 0 0%,
      rgba(var(--boostGlow-9-rgb), 0.2) 10%,
      transparent 100%
    );
  }

  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  z-index: -1;
`

const Title = styled.h3<{ $product: PremiumMeta.PremiumProduct }>`
  color: var(--text-12);
  font-style: normal;
  text-transform: uppercase;
  font-size: 14px;
  font-weight: 600;

  span {
    background: linear-gradient(
      90deg,
      ${({ $product }) =>
          $product === 'subscription'
            ? 'var(--subscriptionGradientFrom-9)'
            : 'var(--boostGradientFrom-9)'}
        50%,
      ${({ $product }) =>
          $product === 'subscription'
            ? 'var(--subscriptionGradientTo-9)'
            : 'var(--boostGradientTo-9)'}
        100%
    );

    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`

const Description = styled.p`
  font-size: 10px;

  color: var(--text-12);
  opacity: 0.45;
  font-style: normal;
  font-weight: 500;
  line-height: 12px;

  width: 280px;
`
